import React, { useRef } from "react";
import z from "zod";

import { AiSettingsSchema } from "@performyard/models";
import { FormHandle, PYForm, VALIDATE_ON, toast } from "@performyard/py-components";

import {
	CheckboxGroup,
	SectionLayout,
	SettingsFormFooter,
	SettingsToggle,
	UnsavedChangesTracker,
} from "../../components";

import { useAiSettings, useUpdateAiSettings } from "../../../../../api/hooks";
import useUnsavedChanges from "../../../../../hooks/useUnsavedChanges";
import { useAiSettingsDefaults } from "../../hooks";

const AIInsightsMeetingsSettingsTab = () => {
	const formRef = useRef<FormHandle<typeof AiSettingsSchema.shape.meetingWorkspaceInsightsSettings>>(null);

	// AI settings data management
	const { mutateAsync: updateAiSettings, isPending } = useUpdateAiSettings();
	const { data, isLoading } = useAiSettings();

	// Unsaved changes tracking
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();

	// toggles
	const isMeetingWorkspaceInsightsAssistantEnabled = data?.meetingWorkspaceInsightsEnabled ?? false;

	// toggle handlers
	const handleMeetingWorkspaceInsightsAssistantToggle = async (val: boolean) => {
		await updateAiSettings({ meetingWorkspaceInsightsEnabled: val });
		toast.success(`Meeting Workspace Insights Assistant ${val === true ? "Enabled" : "Disabled"}.`);
	};

	// Handle form submission and save changes to AI settings
	const handleSave = async ({
		value,
		resetForm,
	}: {
		value: z.infer<typeof AiSettingsSchema.shape.meetingWorkspaceInsightsSettings>;
		resetForm: () => void;
	}) => {
		try {
			await updateAiSettings({ meetingWorkspaceInsightsSettings: value });
			toast.success("Your changes have been saved.");

			// Reset the form normally - it will use the updated defaultValues from the cache
			resetForm();

			// Mark form as safe after successful save
			setIsSafe();
		} catch {
			toast.error("Unable to save settings.");
		}
	};

	// Checkbox options
	const checkBoxOptions = [
		{ value: "progressAndGrowth", label: "Progress & Growth", dataTestId: "progress-and-growth-checkbox" },
		{
			value: "keyAccomplishmentsAndWins",
			label: "Key Accomplishments & Wins",
			dataTestId: "key-accomplishments-and-wins-checkbox",
		},
		{
			value: "developmentAreasAndGrowthOpportunities",
			label: "Development Areas & Growth Opportunities",
			dataTestId: "development-areas-and-growth-opportunities-checkbox",
		},
		{
			value: "notableDiscussionsAndDecisions",
			label: "Notable Discussions & Decisions",
			dataTestId: "notable-discussions-and-decisions-checkbox",
		},
		{ value: "actionItemsAnalysis", label: "Action Item Analysis", dataTestId: "action-items-analysis-checkbox" },
	];

	const defaultValues = useAiSettingsDefaults({
		schema: AiSettingsSchema.shape.meetingWorkspaceInsightsSettings,
		data,
		extractDefaults: (allDefaults) => allDefaults.meetingWorkspaceInsightsSettings,
		extractData: (data) => data.meetingWorkspaceInsightsSettings,
	});

	if (isLoading) {
		return null;
	}

	return (
		<PYForm
			ref={formRef}
			id="ai-insights-meetings-settings-form"
			validateOn={[VALIDATE_ON.ON_SUBMIT]}
			schema={AiSettingsSchema.shape.meetingWorkspaceInsightsSettings}
			defaultValues={defaultValues}
			onSubmit={handleSave}
		>
			<UnsavedChangesTracker
				id="ai-insights-meetings-unsaved-changes"
				setIsUnsafe={setIsUnsafe}
				setIsSafe={setIsSafe}
			/>
			<SectionLayout
				key="meetings-settings-section"
				title="Meeting Workspace Insights Assistant"
				description="The Meeting Workspace Insights Assistant allows participants and admins to generate insights about all the meetings within a meeting workspace."
			>
				<SettingsToggle
					key="meeting-workspace-insights-toggle"
					id="enable-meeting-workspace-insights-assistant"
					label="Enable Meeting Workspace Insights Assistant"
					description="Disabling this setting will hide the ability for participants and admins to generate insights for a meeting workspace."
					checked={isMeetingWorkspaceInsightsAssistantEnabled}
					onCheckedChange={handleMeetingWorkspaceInsightsAssistantToggle}
					dataTestId="meeting-workspace-insights-toggle"
				/>

				<CheckboxGroup
					key="meetings-insights-content-checkboxes"
					title="Insights Content"
					description="Control each section of content inside your meeting workspace insights, to only highlight content that is relevant to you and your company. These options are in addition to an overall summary of the meeting workspace."
					options={checkBoxOptions}
				/>
			</SectionLayout>

			<SettingsFormFooter
				key="meetings-settings-footer"
				formId="ai-insights-meetings-settings-form"
				isPending={isPending}
			/>
		</PYForm>
	);
};

export default AIInsightsMeetingsSettingsTab;
